import queries from '@apis/queries';
import ShopApi from '@apis/shop/ShopApi';
import useTestUser from '@hooks/test/useTestUser';
import useDebouncedValue from '@hooks/useDebouncedValue';
import useResetPage from '@hooks/useResetPage';
import { GetShopsParams } from '@models/shop';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getFlattenPageResponse, getIsBlank, getNextPageParam } from '@utils/slice';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { isMyShopAtom, queryAtom, userLatLonAtom } from '../../../atoms/shopsInMapAtoms';

interface Props {
  locationAgreed: boolean,
  originalItemId?: string,
  isOrder?: boolean
  ready: boolean,
}

function useShops({
    locationAgreed, originalItemId, isOrder, ready,
}: Props) {
    const [page, setPage] = useState<number>(0);
    const query = useAtomValue(queryAtom);
    const debouncedQuery = useDebouncedValue(query);
    const myShop = useAtomValue(isMyShopAtom);
    const { lat, lon } = useAtomValue(userLatLonAtom);
    const params: GetShopsParams = {
        page,
        size: 10,
        lon,
        lat,
        query: debouncedQuery,
        myShop,
        originalItemId,
        locationAgreed,
        searchType: isOrder ? 'ORDER' : 'SEARCH_SHOP',
    };
    const queryInfo = useInfiniteQuery({
        queryKey: queries.shop.listInfinite(params).queryKey,
        queryFn: ({ pageParam }) => ShopApi.getShops({
            ...params,
            page: pageParam,
        }),
        initialPageParam: 0,
        enabled: ((!debouncedQuery) || debouncedQuery.length >= 2)
        && (ready || import.meta.env.MODE === 'development'),
        getNextPageParam,
    });
    const isTestUser = useTestUser();
    const shops = getFlattenPageResponse(queryInfo);
    const isBlank = getIsBlank(queryInfo);

    useResetPage({ params, setPage });
    return {
        ...queryInfo,
        shops: shops?.filter((shop) => isTestUser || !shop.isTestShop),
        isBlank,
    };
}

export default useShops;
